// src/lib/wagmi.js
//
// Wagmi config + the ONE payment path bound to it.
//
// The "chain: undefined (id: 84532)" error came from a payment call that
// built its own client/chain instead of using this config (the printed
// chain had an id but no name — the fingerprint of a hand-rolled
// { id: 84532 } literal). Fix: sendLunexxPayment below is bound to this
// config, so the chains registered here and the chain used for payment
// are the same object by construction.

import { http, createConfig } from 'wagmi';
import { baseSepolia, base } from 'wagmi/chains';
import { injected, coinbaseWallet } from 'wagmi/connectors';
import { sendTransaction, switchChain, getAccount, getChainId } from '@wagmi/core';
import { parseEther } from 'viem';

export const wagmiConfig = createConfig({
  chains: [baseSepolia, base],
  connectors: [
    injected({ shimDisconnect: true }),
    coinbaseWallet({ appName: 'Lunexx', preference: 'all' }),
  ],
  transports: {
    // Pinned RPCs (same endpoints the backend verifies against) instead
    // of wallet-provided defaults — removes a whole class of flaky-RPC
    // behavior during payments.
    [baseSepolia.id]: http('https://sepolia.base.org'),
    [base.id]: http('https://mainnet.base.org'),
  },
});

// ---------------------------------------------------------------------
// The platform payment path. Use this as WalletContext.sendPayment's
// implementation — keep the { to, amount, onSuccess, onError } surface
// your components already call:
//
//   import { sendLunexxPayment } from '../lib/wagmi';
//   const sendPayment = async ({ to, amount, onSuccess, onError }) => {
//     try {
//       const hash = await sendLunexxPayment({ to, amountEth: amount });
//       await onSuccess?.(hash);
//       return hash;
//     } catch (e) { onError?.(e); }
//   };
// ---------------------------------------------------------------------

/** Float-safe ETH string: tiny amounts stringify exponentially
 *  ("5.8e-7"), which parseEther rejects. */
function ethAmountString(amountEth) {
  const n = Number(amountEth);
  if (!Number.isFinite(n) || n <= 0) throw new Error(`Invalid amount: ${amountEth}`);
  return n.toFixed(18).replace(/0+$/, '').replace(/\.$/, '');
}

export async function sendLunexxPayment({ to, amountEth, chainId = baseSepolia.id }) {
  if (!/^0x[a-fA-F0-9]{40}$/.test(to ?? '')) throw new Error(`Invalid recipient: ${to}`);

  const account = getAccount(wagmiConfig);
  if (!account.isConnected) throw new Error('Connect your wallet first');

  // Switch through the config (which knows the chain), not raw provider
  // calls — the connector handles wallet quirks per its own SDK.
  if (getChainId(wagmiConfig) !== chainId) {
    await switchChain(wagmiConfig, { chainId });
  }

  return sendTransaction(wagmiConfig, {
    to,
    value: parseEther(ethAmountString(amountEth)),
    chainId, // resolved against this config's chains — never undefined
  });
}

export default wagmiConfig;